import type { FC } from 'react'
import { useCallback, useId, useState } from 'react'

import {
  contentSection,
  menuCoverImage,
} from '@liveHelper/components/Idle/Idle.twind'
import {
  okButton,
  requestVisitorInformationDescription,
  requestVisitorInformationFormLabel,
  requestVisitorInformationInput,
  requestVisitorInformationPrivacyInfo,
} from '@liveHelper/components/RequestVisitorInformation/RequestVisitorInformation.twind'
import { expandedHeaderImageContainer } from '@liveHelper/components/shared/shared.twind'
import {
  CTACloseButton,
  FireworkLogoWrapper,
  IdleExpandedContainer,
} from '@liveHelper/components/shared/styles'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import {
  actionsSelector,
  paramsSelector,
  useStore,
} from '@liveHelper/models/store'
import {
  EMAIL_INPUT,
  LIVE_HELPER_WIDGET_BASE_CONTAINER,
  SUBMIT_BUTTON,
} from '@liveHelper/testIds'
import {
  useTrackLeadCaptureEmailImpression,
  useTrackLeadCaptureEmailSubmit,
} from '@liveHelper/tracking/useLiveHelperTracking'
import { useGetUtmContent } from '@liveHelper/utils/useGetUtmContent'
import { Close } from '@oneToOne/shared/assets/Close'
import { FireworkBranding } from '@oneToOne/shared/components/FireworkBranding'

import { ImpressionContainer } from '@core/components/ImpressionContainer'
import { Markdown } from '@core/components/Markdown'
import { Button } from '@core/components/twind/Button'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'
import { isMobileWeb } from '@core/userAgent'

interface Props {
  onSubmit: (email: string) => void
}

/**
 * RequestVisitorEmail component
 */
export const RequestVisitorEmail: FC<Props> = ({ onSubmit }) => {
  const { tw } = useTwindContext()
  const { t } = useContextTranslation()
  const emailInputId = useId()

  const params = useStore(paramsSelector)
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)

  const chatChannelConfig = useChatChannelConfig()
  const utmContent = useGetUtmContent()
  const trackLeadCaptureEmailImpression = useTrackLeadCaptureEmailImpression()
  const trackLeadCaptureEmailSubmit = useTrackLeadCaptureEmailSubmit()

  const [email, setEmail] = useState('')
  const [hasError, setHasError] = useState(false)

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault()
      if (!email) {
        return
      }
      if (email.indexOf('@') === -1 || email.indexOf('.') === -1) {
        setHasError(true)
        return
      }
      trackLeadCaptureEmailSubmit({ email })
      onSubmit(email)
    },
    [email, onSubmit, trackLeadCaptureEmailSubmit],
  )

  return (
    <IdleExpandedContainer
      data-testid={LIVE_HELPER_WIDGET_BASE_CONTAINER}
      isMobileWebEnabled={isMobileWeb()}
    >
      <div className={tw?.('absolute')}>
        <ImpressionContainer trackingFn={trackLeadCaptureEmailImpression} />
      </div>
      <CTACloseButton
        shape="circle"
        aria-label={t(`oneToOne:End Conversation`)}
        background="var(--gray-9)"
        buttonSize="sm"
        onClick={(e) => {
          e.stopPropagation()
          sendUIStateTransitionEvent('MINIMIZE')
        }}
        icon={<Close width="16px" fill="var(--white)" label="close" />}
      />
      <div className={tw?.(expandedHeaderImageContainer, 'overflow-hidden')}>
        <div
          className={tw?.(
            menuCoverImage(
              chatChannelConfig?.widget_style?.menu_cover_image_url,
            ),
          )}
        ></div>
      </div>
      <div className={tw?.('px-10', 'py-6')}>
        <div className={tw?.(contentSection, 'flex', 'flex-col', 'gap-2.5')}>
          <p
            className={tw?.(
              requestVisitorInformationDescription,
              'text-sm',
              'font-medium',
              'mb-4',
            )}
          >
            {t(
              'oneToOne:In case we get disconnected, please provide an email where we can reach you.',
            )}
          </p>
          <form onSubmit={handleSubmit} className={tw?.('w-full')}>
            <label
              className={tw?.(
                requestVisitorInformationFormLabel,
                'text-sm',
                'font-medium',
                'mb-4',
              )}
              htmlFor={emailInputId}
            >
              {t(`oneToOne:Email`)}
            </label>
            <input
              ref={(element) => {
                element?.focus()
              }}
              id={emailInputId}
              type="email"
              name="fwVisitorEmail"
              placeholder={t(`oneToOne:Email`)}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setHasError(false)
              }}
              className={tw?.(
                requestVisitorInformationInput,
                'w-full',
                'py-2',
                'px-3',
                'mb-2',
                'border',
                'border-gray-300',
                'focus:border-blue-500',
                'focus:outline-none',
                'rounded-sm',
                hasError && 'border-red-500',
              )}
              data-testid={EMAIL_INPUT}
            />
            <p
              className={tw?.(
                'text-xs',
                'font-medium',
                'mb-2',
                'text-red-500',
                hasError ? 'visible' : 'invisible',
              )}
            >
              {t('oneToOne:Invalid Email')}
            </p>
            {chatChannelConfig?.config?.legal_statement ? (
              <div
                className={tw?.(requestVisitorInformationPrivacyInfo, 'mb-8')}
              >
                <Markdown>{chatChannelConfig.config.legal_statement}</Markdown>
              </div>
            ) : null}
            <div className={tw?.('w-full', 'flex')}>
              <Button
                className={tw?.(okButton)}
                disabled={!email}
                type="submit"
                data-testid={SUBMIT_BUTTON}
                block
              >
                {t(`oneToOne:Start Now`)}
              </Button>
            </div>
          </form>
        </div>
      </div>
      {chatChannelConfig?.widget_style?.firework_branding ? (
        <FireworkLogoWrapper>
          <FireworkBranding
            dark={true}
            utmContent={utmContent}
            lang={params.lang}
          />
        </FireworkLogoWrapper>
      ) : null}
    </IdleExpandedContainer>
  )
}
